"use client"

import React from "react"
import { Card, CardContent } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Calendar, Clock, ArrowRight, User, Search, X, SlidersHorizontal } from "lucide-react"
import { useState } from "react"
import { Input } from "@/components/ui/input"
import { useLanguage } from "../contexts/language-context"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu"
import Link from "next/link"

// Define blog post type
type BlogPost = {
  id: string
  title: string
  excerpt: string
  category: string
  author: string
  date: string
  readTime: string
  image: string
  tags: string[]
  gradient: string
  views?: number
  slug: string
  draft?: boolean
}

type BlogProps = {
  // Force the locale used for formatting/links to avoid hydration drift
  language?: string
}

export default function Blog(props: BlogProps = {}) {
  const { t, language: ctxLanguage } = useLanguage()
  // Prefer the locale passed from the server page; fall back to context
  const language = (props.language as string) || ctxLanguage
  const [selectedCategory, setSelectedCategory] = useState("All")
  const [selectedTag, setSelectedTag] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState("")
  const [sortBy, setSortBy] = useState<"newest" | "oldest" | "popular">("newest")

  // Blog posts data
  const blogPosts: BlogPost[] = [
    {
      id: "1",
      title: "The 'Digital Twin': How We're Turning Abstract Data into Interactive 3D Web Applications",
      excerpt:
        "The web is evolving from flat 2D pages to immersive 3D experiences. Learn how the 'Digital Twin' concept is solving high-value problems for manufacturing, real estate, and research by blending full-stack development, data architecture, and 3D visualization.",
      
      category: "3D Visualization",
      author: "David X Avila",
      date: "2025-10-23",
      readTime: "5 min read",
      image: language === "en" 
        ? "/images/blog/featured/digital-twin-en.png"
        : "/images/blog/featured/digital-twin-es.png",
      tags: [
        "Digital Twin",
        "3D Visualization",
        "Full-Stack Development",
        "Data Architecture",
        "3D Product Configurator",
        "Real Estate Tech",
        "Immersive Web"
      ],
      gradient: "from-gray-700 via-gray-900 to-black",
      views: 0,
      slug: "digital-twin-abstract-data-3d-web-apps",
    },
    {      id: "9",
      title: "",
      excerpt: "",
      category: "Systems",
      author: "David X. Avila",
      date: "2026-02-05",
      readTime: "7 min read",
      image: "/images/blog/featured/orchestrator-protocol-system-architecture-en.jpg",
      tags: [],
      gradient: "from-slate-700 via-slate-900 to-black",
      views: 0,
      slug: "orchestrator-protocol",
      draft: false,
    },
    {      id: "2",
      title: "Creating Photorealistic 3D Renders: A Complete Guide",
      excerpt:
        "Learn the essential techniques and tools for creating stunning photorealistic 3D visualizations that captivate clients and audiences.",
      
      category: "3D Design",
      author: "David X. Avila",
      date: "2024-01-10",
      readTime: "8 min read",
      image: "/placeholder.svg?height=300&width=500&text=3D+Rendering+Guide",
      tags: ["Blender", "V-Ray", "3D Modeling", "Rendering"],
      gradient: "from-teal-500 to-green-500",
      views: 980,
      slug: "photorealistic-3d-renders-complete-guide",
      draft: false,
    },
    {
      id: "3",
      title: "Database Optimization Strategies for High-Performance Apps",
      excerpt:
        "Discover proven techniques to optimize database performance, reduce query times, and scale your applications effectively.",
      
      category: "Database",
      author: "David X. Avila",
      date: "2024-01-05",
      readTime: "6 min read",
      image: "/placeholder.svg?height=300&width=500&text=Database+Optimization",
      tags: ["PostgreSQL", "MongoDB", "Performance", "Scaling"],
      gradient: "from-purple-500 to-pink-500",
      views: 1560,
      slug: "database-optimization-strategies",
      draft: true,
    },
    {
      id: "4",
      title: "Brand Identity Design: From Concept to Implementation",
      excerpt:
        "A comprehensive look at the brand identity design process, from initial research to final implementation across all touchpoints.",
      
      category: "Design",
      author: "David X. Avila",
      date: "2023-12-28",
      readTime: "7 min read",
      image: "/placeholder.svg?height=300&width=500&text=Brand+Identity+Design",
      tags: ["Branding", "Logo Design", "Identity", "Creative"],
      gradient: "from-pink-500 to-rose-500",
      views: 870,
      slug: "brand-identity-design-concept-implementation",
      draft: true,
    },
    {
      id: "5",
      title: "Academic Writing in the Digital Age: Tools and Techniques",
      excerpt:
        "Modern approaches to academic writing, including digital tools, collaboration platforms, and best practices for research papers.",
      
      category: "Writing",
      author: "David X. Avila",
      date: "2023-12-20",
      readTime: "4 min read",
      image: "/placeholder.svg?height=300&width=500&text=Academic+Writing",
      tags: ["LaTeX", "Research", "Writing", "Academia"],
      gradient: "from-indigo-500 to-blue-500",
      views: 650,
      slug: "academic-writing-digital-age",
      draft: true,
    },
    {
      id: "6",
      title: "Building Scalable APIs with Modern Architecture Patterns",
      excerpt:
        "Explore microservices, serverless architecture, and API design patterns that enable scalable and maintainable backend systems.",
      
      category: "Backend",
      author: "David X. Avila",
      date: "2023-12-15",
      readTime: "9 min read",
      image: "/placeholder.svg?height=300&width=500&text=API+Architecture",
      tags: ["API", "Microservices", "Node.js", "Architecture"],
      gradient: "from-orange-500 to-red-500",
      views: 1120,
      slug: "scalable-apis-modern-architecture",
      draft: true,
    },
    {
      id: "7",
      title: "Machine Learning for Web Developers: A Practical Introduction",
      excerpt:
        "Learn how to integrate machine learning capabilities into your web applications without being a data science expert.",
      
      category: "Web Development",
      author: "David X. Avila",
      date: "2023-11-28",
      readTime: "10 min read",
      image: "/placeholder.svg?height=300&width=500&text=ML+for+Web+Devs",
      tags: ["Machine Learning", "JavaScript", "TensorFlow.js", "AI"],
      gradient: "from-blue-500 to-cyan-500",
      views: 1850,
      slug: "machine-learning-web-developers",
      draft: true,
    },
    {
      id: "8",
      title: "Effective Data Visualization Techniques for Complex Datasets",
      excerpt:
        "Discover how to transform complex data into clear, insightful visualizations that tell compelling stories.",
      
      category: "Data",
      author: "David X. Avila",
      date: "2023-11-15",
      readTime: "7 min read",
      image: "/placeholder.svg?height=300&width=500&text=Data+Visualization",
      tags: ["Data Viz", "D3.js", "Charts", "Analytics"],
      gradient: "from-green-500 to-teal-500",
      views: 920,
      slug: "data-visualization-techniques",
      draft: true,
    },
  ]

  const getPostTags = (post: BlogPost): string[] => {
    try {
      const raw = (t as any).raw?.(`blog.posts.${post.slug}.tags`)
      if (Array.isArray(raw) && raw.length > 0) return raw.map(String)
    } catch (e) {
      // Fallback to inline tags.
    }
    return post.tags
  }

  // Extract all unique categories
  const categories = [
    "All",
    ...Array.from(new Set(blogPosts.filter((p) => !p.draft).map((post) => post.category))),
  ]

  // Extract all unique tags
  const allTags = Array.from(new Set(blogPosts.filter((p) => !p.draft).flatMap((post) => getPostTags(post))))

  // Filter and sort posts
  const filteredAndSortedPosts = blogPosts
    // Hide drafts by default
    .filter((post) => !post.draft)
    .filter((post) => {
      // Filter by category
      const categoryMatch = selectedCategory === "All" || post.category === selectedCategory

      // Filter by tag
      const localizedTags = getPostTags(post)
      const tagMatch = !selectedTag || localizedTags.includes(selectedTag)

      // Filter by search query (use localized titles/excerpts from messages when available)
      const localizedTitle = t(`blog.posts.${post.slug}.title`) || post.title
      const localizedExcerpt = t(`blog.posts.${post.slug}.excerpt`) || post.excerpt
      const searchMatch =
        !searchQuery ||
        localizedTitle.toLowerCase().includes(searchQuery.toLowerCase()) ||
        localizedExcerpt.toLowerCase().includes(searchQuery.toLowerCase()) ||
        localizedTags.some((tag) => tag.toLowerCase().includes(searchQuery.toLowerCase()))

      return categoryMatch && tagMatch && searchMatch
    })
    .sort((a, b) => {
      if (sortBy === "newest") {
        return new Date(b.date).getTime() - new Date(a.date).getTime()
      } else if (sortBy === "oldest") {
        return new Date(a.date).getTime() - new Date(b.date).getTime()
      } else if (sortBy === "popular") {
        return (b.views || 0) - (a.views || 0)
      }
      return 0
    })

  // Reset filters
  const resetFilters = () => {
    setSelectedCategory("All")
    setSelectedTag(null)
    setSearchQuery("")
    setSortBy("newest")
  }

  // Get post title based on language
  const getPostTitle = (post: BlogPost) => {
    // Prefer translated title from messages if present, otherwise fall back to inline title
    return t(`blog.posts.${post.slug}.title`) || post.title
  }

  // Get post excerpt based on language
  const getPostExcerpt = (post: BlogPost) => {
    return t(`blog.posts.${post.slug}.excerpt`) || post.excerpt
  }

  return (
    <section
      id="blog"
      className="relative py-20 px-6 min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/50 to-purple-50/30 dark:from-slate-900 dark:via-slate-800 dark:to-slate-700"
    >
      {/* Enhanced background elements for high resolution */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-50/90 via-blue-50/60 to-purple-50/40 dark:from-slate-900/95 dark:via-slate-800/80 dark:to-slate-700/70" />
      <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-r from-transparent via-sky-100/20 to-transparent dark:via-sky-900/10" />
      <div className="absolute top-20 right-10 w-96 h-96 bg-gradient-to-br from-sky-200/30 to-purple-200/30 dark:from-sky-800/20 dark:to-purple-800/20 rounded-full blur-3xl" />
      <div className="absolute bottom-20 left-10 w-80 h-80 bg-gradient-to-br from-purple-200/30 to-pink-200/30 dark:from-purple-800/20 dark:to-pink-800/20 rounded-full blur-3xl" />

      <div className="container mx-auto max-w-7xl relative z-10">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-high-contrast mb-6">
            {t("blog.title")} <span className="gradient-text">{t("blog.subtitle")}</span>
          </h2>
          <p className="text-xl text-medium-contrast max-w-3xl mx-auto">{t("blog.description")}</p>
        </div>

        {/* Search and Filter Bar */}
        <div className="mb-8 flex flex-col md:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder={t("blog.search")}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
            {searchQuery && (
              <Button
                variant="ghost"
                size="sm"
                className="absolute right-1 top-1/2 transform -translate-y-1/2 h-7 w-7 p-0"
                onClick={() => setSearchQuery("")}
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>

          <div className="flex gap-2">
            {/* Sort Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="flex gap-2 bg-transparent">
                  <SlidersHorizontal className="h-4 w-4" />
                  {t("blog.sortBy")}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => setSortBy("newest")}>{t("blog.newest")}</DropdownMenuItem>
                <DropdownMenuItem onClick={() => setSortBy("oldest")}>{t("blog.oldest")}</DropdownMenuItem>
                <DropdownMenuItem onClick={() => setSortBy("popular")}>{t("blog.popular")}</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Reset Filters */}
            {(selectedCategory !== "All" || selectedTag || searchQuery) && (
              <Button variant="ghost" onClick={resetFilters}>
                {t("blog.clearFilters")}
              </Button>
            )}
          </div>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-3 mb-8">
          {categories.map((category) => (
            <Button
              key={category}
              variant={category === selectedCategory ? "default" : "outline"}
              size="sm"
              onClick={() => setSelectedCategory(category)}
              className={`${
                category === selectedCategory
                  ? "bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700 text-white"
                  : "hover:bg-sky-50 dark:hover:bg-sky-900/20 hover:text-sky-600 dark:hover:text-sky-400"
              } transition-all duration-200`}
            >
              {category === "All" ? t("blog.category.all") : category}
            </Button>
          ))}
        </div>

        {/* Tags Filter */}
        <div className="flex flex-wrap justify-center gap-2 mb-12">
          <span className="text-sm text-medium-contrast mr-2">{t("blog.filterByTag")}:</span>
          {allTags.slice(0, 12).map((tag) => (
            <Badge
              key={tag}
              variant={selectedTag === tag ? "default" : "outline"}
              className={`cursor-pointer ${
                selectedTag === tag
                  ? "bg-sky-500 hover:bg-sky-600"
                  : "hover:bg-sky-50 dark:hover:bg-sky-900/20 hover:text-sky-600 dark:hover:text-sky-400"
              }`}
              onClick={() => setSelectedTag(selectedTag === tag ? null : tag)}
            >
              {tag}
              {selectedTag === tag && <X className="ml-1 h-3 w-3" />}
            </Badge>
          ))}
        </div>

        {/* Featured Post */}
        {filteredAndSortedPosts.length > 0 && (
          <div className="mb-16">
            <Card className="overflow-hidden hover:shadow-2xl transition-all duration-300 bg-card/95 backdrop-blur-sm border-border">
              <div className="grid lg:grid-cols-2 gap-0">
                <div className="relative overflow-hidden">
                  <img
                    src={filteredAndSortedPosts[0].image || "/placeholder.svg"}
                    alt={getPostTitle(filteredAndSortedPosts[0])}
                    className="w-full h-64 lg:h-full object-cover hover:scale-105 transition-transform duration-300"
                  />
                  <div
                    className={`absolute top-4 left-4 px-3 py-1 rounded-full text-xs font-medium text-white bg-gradient-to-r ${filteredAndSortedPosts[0].gradient}`}
                  >
                    {t("blog.featured")}
                  </div>
                </div>
                <CardContent className="p-8 flex flex-col justify-center">
                  <Badge variant="secondary" className="w-fit mb-4">
                    {filteredAndSortedPosts[0].category}
                  </Badge>
                  <h3 className="text-2xl lg:text-3xl font-bold text-high-contrast mb-4 leading-tight">
                    {getPostTitle(filteredAndSortedPosts[0])}
                  </h3>
                  <p className="text-medium-contrast mb-6 text-lg leading-relaxed">
                    {getPostExcerpt(filteredAndSortedPosts[0])}
                  </p>
                  <div className="flex items-center gap-4 text-sm text-low-contrast mb-6">
                    <div className="flex items-center gap-1">
                      <User className="h-4 w-4" />
                      {filteredAndSortedPosts[0].author}
                    </div>
                    <div className="flex items-center gap-1">
                      <Calendar className="h-4 w-4" />
                      {new Date(filteredAndSortedPosts[0].date).toLocaleDateString(language, { year: 'numeric', month: '2-digit', day: '2-digit' })}
                    </div>
                    <div className="flex items-center gap-1">
                      <Clock className="h-4 w-4" />
                      {filteredAndSortedPosts[0].readTime}
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2 mb-6">
                    {getPostTags(filteredAndSortedPosts[0]).map((tag, idx) => (
                      <Badge
                        key={idx}
                        variant="outline"
                        className="text-xs cursor-pointer hover:bg-sky-50 dark:hover:bg-sky-900/20"
                        onClick={() => setSelectedTag(tag)}
                      >
                        {tag}
                      </Badge>
                    ))}
                  </div>
                  <Link href={`/${language}/blog/${filteredAndSortedPosts[0].slug}`} passHref>
                    <Button className="w-fit bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700 text-white">
                      {t("blog.readFull")}
                      <ArrowRight className="h-4 w-4 ml-2" />
                    </Button>
                  </Link>
                </CardContent>
              </div>
            </Card>
          </div>
        )}

        {/* Blog Grid */}
        {filteredAndSortedPosts.length > 1 && (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredAndSortedPosts.slice(1).map((post, index) => (
              <Card
                key={post.id}
                className="group overflow-hidden hover:shadow-xl transition-all duration-300 bg-card/95 backdrop-blur-sm border-border"
              >
                <div className="relative overflow-hidden">
                  <img
                    src={post.image || "/placeholder.svg"}
                    alt={getPostTitle(post)}
                    className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div
                    className={`absolute top-4 left-4 px-3 py-1 rounded-full text-xs font-medium text-white bg-gradient-to-r ${post.gradient}`}
                  >
                    {post.category}
                  </div>
                </div>
                <CardContent className="p-6">
                  <h3 className="text-xl font-semibold text-high-contrast mb-3 leading-tight group-hover:text-sky-600 dark:group-hover:text-sky-400 transition-colors">
                    {getPostTitle(post)}
                  </h3>
                  <p className="text-medium-contrast mb-4 text-sm leading-relaxed">{getPostExcerpt(post)}</p>
                  <div className="flex items-center gap-3 text-xs text-low-contrast mb-4">
                    <div className="flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      {new Date(post.date).toLocaleDateString(language, { year: 'numeric', month: '2-digit', day: '2-digit' })}
                    </div>
                    <div className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {post.readTime}
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1 mb-4">
                    {getPostTags(post).slice(0, 3).map((tag, idx) => (
                      <Badge
                        key={idx}
                        variant="outline"
                        className="text-xs cursor-pointer hover:bg-sky-50 dark:hover:bg-sky-900/20"
                        onClick={(e) => {
                          e.preventDefault()
                          setSelectedTag(tag)
                        }}
                      >
                        {tag}
                      </Badge>
                    ))}
                  </div>
                  <Link href={`/${language}/blog/${post.slug}`} passHref>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="w-full justify-between hover:bg-sky-50 dark:hover:bg-sky-900/20 hover:text-sky-600 dark:hover:text-sky-400"
                    >
                      {t("blog.readMore")}
                      <ArrowRight className="h-4 w-4" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* No posts message */}
        {filteredAndSortedPosts.length === 0 && (
          <div className="text-center py-12">
            <p className="text-xl text-medium-contrast">{t("blog.noResults")}</p>
            <Button variant="outline" onClick={resetFilters} className="mt-4 bg-transparent">
              {t("blog.viewAll")}
            </Button>
          </div>
        )}

        {/* Load More Button */}
        {filteredAndSortedPosts.length > 1 && (
          <div className="text-center mt-12">
            <Button
              variant="outline"
              size="lg"
              className="hover:bg-sky-50 dark:hover:bg-sky-900/20 hover:text-sky-600 dark:hover:text-sky-400 hover:border-sky-300 dark:hover:border-sky-600 bg-transparent"
            >
              {t("blog.loadMore")}
            </Button>
          </div>
        )}
      </div>
    </section>
  )
}
